import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getOverviewRequest,
  getSalesSummaryRequest,
  getGoodsRequest,
  getCurrentOrdersRequest,
  getSalesOrdersRequest,
  getSalesTerritoriesRequest,
  getShipmentsRequest,
  getProductMetadataRequest
} from "../../api";

import { salesOrdersFormatter } from "../SalesOrders/SalesOrdersFormatter"

const initialState = {
  overview: [],
  overviewExport: null,
  salesSummary: [],
  goods: [],
  currentOrders: [],
  salesOrders: [],
  salesTerritories: [],
  shipments: [],
  productMetadata: {},
  getOverviewInProgress: false,
  getOverviewError: null,
  getOverviewExportInProgress: false,
  getOverviewExportError: null,
  getSalesSummaryInProgress: false,
  getSalesSummaryError: null,
  getGoodsInProgress: false,
  getGoodsError: null,
  getSalesOrderInProgress: false,
  getSalesOrderError: null,
  getSalesTerritoriesInProgress: false,
  getSalesTerritoriesError: null,
  getShipmentsInProgress: false,
  getShipmentsError: null,
  getProductMetadataInProgress: false,
  getProductMetadataError: null

};

export const getOverview = createAsyncThunk(
  "overview/getOverview",
  async (_, { rejectWithValue }) => {
	try {
	  const { page, search, sort, pageSize } = _;
	  const response = await getOverviewRequest({
			page,
			search,
			sort,
			pageSize
	  });
	  return response.data;
	} catch (error) {
	  return rejectWithValue(error.message);
	}
  },
);

export const getOverviewExport = createAsyncThunk(
  "overview/getOverviewExport",
  async (_, { rejectWithValue }) => {
	try {
	  const { search, sort } = _;
	  const response = await getOverviewRequest({
		page: 1,
		search,
		sort,
		all: true
	  });
	  return response.data;
	} catch (error) {
	  return rejectWithValue(error.message);
	}
  },
);


export const getSalesSummary = createAsyncThunk(
  "overview/getSalesSummary",
  async (_, { rejectWithValue }) => {
	try {
	  const { upc, type, page, search, sort } = _;
	  const response = await getSalesSummaryRequest({
	  	upc,
		type,  
		page,
		search,
		sort,
	  });
	  return response.data;
	} catch (error) {
	  return rejectWithValue(error.message);
	}
  },
);

export const getGoods = createAsyncThunk(
  "overview/getGoods",
  async (_, { rejectWithValue }) => {
	try {
	  const { upc, page, search, sort } = _;
	  const response = await getGoodsRequest({
		upc,
		page,
		search,
		sort,
	  });
	  return response.data;
	} catch (error) {
	  return rejectWithValue(error.message);
	}
  },
);

export const getCurrentOrders = createAsyncThunk(
  "overview/getCurrentOrders",
  async (_, { rejectWithValue }) => {
	try {
	  const { upc, page, search, sort } = _;
	  const response = await getCurrentOrdersRequest({
		upc,
		page,
		search,
		sort,
	  });
	  return response.data;
	} catch (error) {
	  return rejectWithValue(error.message);
	}
  },
);

export const getSalesOrders = createAsyncThunk(
  "overview/getSalesOrders",
  async (_, { rejectWithValue }) => {
	try {
	  const { upc, start, end, mode, page, search, sort } = _;
	  const response = await getSalesOrdersRequest({
		upc,
		start,
		end,
		mode,
		page,
		search,
		sort,
	  });
	  return response.data;
	} catch (error) {
	  return rejectWithValue(error.message);
	}
  },
);

export const getSalesTerritories = createAsyncThunk(
  "overview/getSalesTerritories",
  async (_, { rejectWithValue }) => {
	try {
	  const { upc, page, search, sort, page_size } = _;
	  const response = await getSalesTerritoriesRequest({
		upc,
		page,
		search,
		sort,
		page_size
	  });
	  return response.data;
	} catch (error) {
	  return rejectWithValue(error.message);
	}
  },
);

export const getShipments = createAsyncThunk(
  "overview/getShipments",
  async (_, { rejectWithValue }) => {
	try {
	  const { upc, type, page, search, sort } = _;
	  const response = await getShipmentsRequest({
		upc,
		type,
		page,
		search,
		sort,
	  });
	  return response.data;
	} catch (error) {
	  return rejectWithValue(error.message);
	}
  },
);

export const getProductMetadata = createAsyncThunk(
  "overview/getProductMetadata",
  async (upc, { rejectWithValue }) => {
	try {
	  const response = await getProductMetadataRequest(upc);
	  return response.data && response.data.release ? response.data.release : {};
	} catch (error) {
	  return rejectWithValue(error.message);
	}
  },
);

export const dashboardSlice = createSlice({
  name: "overview",
  initialState,
  reducers: {
	clearData(state) {
	  state.overview = [];
	  state.salesTerritories = [];
	  state.shipments = [];
	  state.productMetadata = {};
	},
  },
  extraReducers: (builder) => {
	builder
	  .addCase(getOverview.pending, (state) => {
		state.getOverviewInProgress = true;
		state.getOverviewError = null;
	  })
	  .addCase(getOverview.fulfilled, (state, action) => {
	  	const { payload =[] } = action;
		let totalPages = 0;
		if(Array.isArray(payload) && payload.length > 0) {
			totalPages = payload[0].total_rows;
		}  
		state.overview = {data: payload, totalPages};  
		state.getOverviewInProgress = false;
		state.getOverviewError = null;
	  })
	  .addCase(getOverview.rejected, (state, action) => {
		state.getOverviewInProgress = false;
		state.getOverviewError = action.payload;
	  })
	  
	  .addCase(getOverviewExport.pending, (state) => {
		state.getOverviewExport = null;
		state.getOverviewExportInProgress = true;
		state.getOverviewExportError = null;
		})
		.addCase(getOverviewExport.fulfilled, (state, action) => {
		state.overviewExport = action.payload;  
		state.getOverviewExportInProgress = false;
		state.getOverviewExportError = null;
		})
		.addCase(getOverviewExport.rejected, (state, action) => {
		state.getOverviewExportInProgress = false;
		state.getOverviewExportError = action.payload;
		})

	  
	  .addCase(getSalesSummary.pending, (state) => {
  		state.getSalesSummaryInProgress = true;
  		state.getSalesSummaryError = null;
	  })
	  .addCase(getSalesSummary.fulfilled, (state, action) => {
  		const sales = {data: action.payload.slice(0, 10), total_rows: action.payload.length};  
  		state.salesSummary = sales;
  		state.getSalesSummaryInProgress = false;
  		state.getSalesSummaryError = null;
	  })
	  .addCase(getSalesSummary.rejected, (state, action) => {
  		state.getSalesSummaryInProgress = false;
  		state.getSalesSummaryError = action.payload;
	  })
	  .addCase(getGoods.pending, (state) => {
		state.getGoodsInProgress = true;
		state.getGoodsError = null;
	  })
	  .addCase(getGoods.fulfilled, (state, action) => {
		const goods = {data: action.payload.slice(0, 10), total_rows: action.payload.length};  
		state.goods = goods;
		state.getGoodsInProgress = false;
		state.getGoodsError = null;
	  })
	  .addCase(getGoods.rejected, (state, action) => {
		state.getGoodsInProgress = false;
		state.getGoodsError = action.payload;
	  })
	  .addCase(getCurrentOrders.pending, (state) => {
		state.getCurrentOrdersInProgress = true;
		state.getCurrentOrdersError = null;
	  })
	  .addCase(getCurrentOrders.fulfilled, (state, action) => {
		const orders = {data: action.payload.slice(0, 10), total_rows: action.payload.length};  
		state.currentOrders = orders;
		state.getCurrentOrdersInProgress = false;
		state.getCurrentOrdersError = null;
	  })
	  .addCase(getCurrentOrders.rejected, (state, action) => {
		state.getCurrentOrdersInProgress = false;
		state.getCurrentOrdersError = action.payload;
	  })
	  .addCase(getSalesOrders.pending, (state) => {
		state.getSalesOrdersInProgress = true;
		state.getSalesOrdersError = null;
		})
	  .addCase(getSalesOrders.fulfilled, (state, action) => {
		const data = salesOrdersFormatter(action.payload);
		const sales = {data, total_rows: data.length};  
		state.salesOrders = sales;
		state.getSalesOrdersInProgress = false;
		state.getSalesOrdersError = null;
	  })
	  .addCase(getSalesOrders.rejected, (state, action) => {
		state.getSalesOrdersInProgress = false;
		state.getSalesOrdersError = action.payload;
	  })
	  .addCase(getSalesTerritories.pending, (state) => {
		state.getSalesTerritoriesInProgress = true;
		state.getSalesTerritoriesError = null;
	  })
	  .addCase(getSalesTerritories.fulfilled, (state, action) => {
		const sales = {data: action.payload, total_rows: action.payload.length};  
		state.salesTerritories = sales;
		state.getSalesTerritoriesInProgress = false;
		state.getSalesTerritoriesError = null;
	  })
	  .addCase(getSalesTerritories.rejected, (state, action) => {
		state.getSalesTerritoriesInProgress = false;
		state.getSalesTerritoriesError = action.payload;
	  })
	  .addCase(getShipments.pending, (state) => {
		state.getShipmentsInProgress = true;
		state.getShipmentsError = null;
	  })
	  .addCase(getShipments.fulfilled, (state, action) => {
		let data = Array.isArray(action.payload)?action.payload:[];
		data = data.map(item=>{
			item.Key = [item.Territory, item.Customer, item.InvoiceShipLast].join('_');
			return item;
		})  
		const shipments = {data, total_rows: data.length};  
		state.shipments = shipments;
		state.getShipmentsInProgress = false;
		state.getShipmentsError = null;
	  })
	  .addCase(getShipments.rejected, (state, action) => {
		state.getShipmentsInProgress = false;
		state.getShipmentsError = action.payload;
	  })
	  .addCase(getProductMetadata.pending, (state) => {
		  state.productMetadata = {};
		  state.getProductMetadataInProgress = true;
		  state.getProductMetadataError = null;
		  })
		.addCase(getProductMetadata.fulfilled, (state, action) => {
		  state.productMetadata = action.payload;
		  state.getProductMetadataInProgress = false;
		  state.getProductMetadataError = null;
		})
		.addCase(getProductMetadata.rejected, (state, action) => {
		  state.getProductMetadataInProgress = false;
		  state.getProductMetadataError = action.payload;
		})

  },
});

export const { clearData } = dashboardSlice.actions;

// this is for configureStore
export default dashboardSlice.reducer;
